import React, { useEffect, Fragment, useRef } from "react";
import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from '@material-ui/styles';
import { Button, Grid, Typography, AppBar, Toolbar, Tooltip, IconButton, Link, Box, Icon } from '@material-ui/core';
import CloseOutlinedIcon from '@material-ui/icons/CloseOutlined';
import { getImageMapData } from "../../../../store/global/selectors";
import BottomDrawer from "../../../atoms/bottomDrawer";
import ImageSelector from "./ImageSelector";
import AssetsExplorerDrawer from "./AssetsExplorerDrawer";
import { setImageMapData, triggerTour } from "../../../../store/global/actions";
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CssBaseline from '@material-ui/core/CssBaseline';
import SelectedAreaDrawer from "./SelectedAreaDrawer.js";
import AreaPropertiesDrawer from "./AreaPropertiesDrawer";
import Colors from "../../../../config/colors";
import ContactSupportIcon from '@material-ui/icons/ContactSupport';

const useStyles = makeStyles((theme) => ({
    drawerTitle: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexGrow: 1,
        color: '#2c3b4a'
    },
    right: {
        marginLeft: 'auto',
        textAlign: 'right'
    },
    progressBar: {
        width: '100%',
        '& > * + *': {
            marginTop: theme.spacing(2),
        },
    },
    toolBarHeading: {
        color: '#fff',
        marginTop: 10
    },
    drawerBody: {
        height: 'calc(100vh - 128px)',
        padding: '1.5rem',
        flexGrow: 1,
        overflow: 'auto',
        background: '#D9D9D9'
    },
    noImage: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    importBtn: {
        backgroundColor: '#E8E8E8',
        borderRadius: '0px',
        padding: '16px 48px',
        marginTop: '6px',
        textTransform: 'none',
        fontSize: '1rem',
        "&:focus": {
            outline: "none",
        },
        "&.Mui-focusVisible": {
            boxShadow: "none",
            border: "none",
        },
    },
    toolsButton: {
        borderRadius: '3px',
        padding: '5px',
    },
    selectedAreaExpandButton: {
        position: 'absolute',
        top: '50%',
        left: '0',
        transform: 'translateY(-50%)',
        borderRadius: '0px 10px 10px 0',
        background: '#3f4e5c',
        outline: 'none',
        width: '2rem',
        zIndex: 3,
        color: '#fff',
        transition: theme.transitions.create('left', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        '&:hover': {
            background: '#3f4e5c'
        }
    },
    submitBtn: {
        background: Colors.primary,
        color: '#fff',
        transition: 'all 0.3s ease',

        '&:hover': {
            background: '#3f4e5c'
        }
    }, 
    textCapitalise: {
        textTransform: 'capitalize'
    }
}));

const ImageMapEditor = () => {
    const drawerBodyRef = useRef();
    const dispatch = useDispatch();
    const { selectedTool, open, asset, id } = useSelector(getImageMapData);

    const classes = useStyles();

    const [openAssetsExplorer, setOpenAssetsExplorer] = useState(false);
    const [isLeftDrawerOpened, setLeftDrawerOpened] = useState(false);
    const [isFetching, setIsFetching] = useState(false);

    // function for handling the tool selection. This function will set the tool name into the redux state
    const handleToolSelection = (tool) => {
        if (!selectedTool) {
            dispatch(setImageMapData({ selectedTool: tool }))
        } else {
            dispatch(setImageMapData({ selectedTool: null }))
        }
    }

    const handleSelectedAreaDrawerExpansion = () => {
        setLeftDrawerOpened(!isLeftDrawerOpened)
    }

    const onCloseEditor = () => {
        setLeftDrawerOpened(false);
        dispatch(setImageMapData({ open: false }));
        setOpenAssetsExplorer(false);
    }

    const onCloseAssetsExplorer = () => {
        setOpenAssetsExplorer(false);
    }

    const onSubmit = () => {
        dispatch(setImageMapData({ open: false }));
        setOpenAssetsExplorer(false);
        setLeftDrawerOpened(false);
        if (id !== undefined && id !== null) {
            // updateImageMapUiWidget();
        }
    }

    const onChangeImage = () => {
        setOpenAssetsExplorer(true);
    }


    useEffect(() => {
        if (open) {
            setTimeout(() => {
                setLeftDrawerOpened(true)
            }, 300)
        }
    }, [open])

    const handleManualTour = () => {
        dispatch(triggerTour({currentTour: 'imageMapEditor', initStart: true}))
    }

    
    return (
        <Fragment>
            <CssBaseline />
            <BottomDrawer
                open={open}
                height='calc(100vh - 64px)'
                maxHeight='100%'
            >
                <div>
                    <AppBar position='relative' style={{ backgroundColor: "#fff" }}>
                        <Toolbar>
                            <Grid container spacing={1}>
                                <Grid item xs={3}>
                                    {asset && <Button data-ui-tour-id="change-image-btn" onClick={onChangeImage} variant="outlined" id={"changeImageButton"} style={{ marginRight: '0.5rem' }} className={classes.textCapitalise} >Change Image</Button>}

                                    <IconButton
                                        className={classes.toolsButton}
                                        data-ui-tour-id="draw-area-tool"
                                        style={{ background: selectedTool === 'polygon' ? 'rgba(0, 0, 0, 0.04)' : 'transparent' }}
                                        onClick={() => handleToolSelection('polygon')}
                                        disabled={!asset}
                                        id={"createAreaButton"}
                                    >
                                        <Tooltip title='Create Polygonal areas'>
                                            <img style={{ width: '2rem', height: '2rem' }} src={`${process.env.PUBLIC_URL}/ide_icons/lasso.svg`} />
                                        </Tooltip>
                                    </IconButton>
                                </Grid>
                                <Grid item xs={6} className={classes.drawerTitle}>
                                    <Typography variant="h6" >
                                        Image Map Editor
                                    </Typography>
                                </Grid>
                                <Grid item xs={3} className={classes.right}>
                                    <Box style={{display: 'flex', gap: '0.5rem', alignItems: 'center', justifyContent: "flex-end"}}>
                                        <Tooltip title='Show Tour'>
                                            <Icon onClick={handleManualTour} style={{height: '2rem', width: 'auto', display: 'flex',justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}>
                                              <ContactSupportIcon style={{fontSize: '2rem', color: 'black'}}/>
                                            </Icon>
                                        </Tooltip>
                                        <Button data-ui-tour-id="update-map-btn" onClick={onSubmit} variant='contained' className={`${classes.submitBtn} ${classes.textCapitalise}`}>Update</Button>
                                        <IconButton data-ui-tour-id="close-map-btn" onClick={onCloseEditor} id="closeImageMapEditor">
                                            <Tooltip title="Close image map editor">
                                                <CloseOutlinedIcon size={'small'} button />
                                            </Tooltip>
                                        </IconButton>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Toolbar>
                    </AppBar>
                    <AssetsExplorerDrawer open={openAssetsExplorer} onClose={onCloseAssetsExplorer} />
                    <AreaPropertiesDrawer />
                    <main id="imageMapEditorBody" style={{ position: 'relative' }}>
                        {/* {open &&
                            <Tooltip title={isLeftDrawerOpened ? 'Close selected area list' : 'Open selected area list'}>
                                <IconButton
                                    className={classes.selectedAreaExpandButton}
                                    onClick={handleSelectedAreaDrawerExpansion}
                                    style={{ left: isLeftDrawerOpened ? '15rem' : '0' }}
                                >
                                    <ChevronRightIcon
                                        style={{
                                            transform: isLeftDrawerOpened ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'all 0.3s ease'
                                        }}
                                        button
                                    />
                                </IconButton>
                            </Tooltip>
                        } */}
                        <Grid className={classes.drawerBody} ref={drawerBodyRef} container spacing={1}>
                            {/* <SelectedAreaDrawer isOpened={isLeftDrawerOpened} isFetching={isFetching} /> */}
                            {open && <ImageSelector  isFetching={isFetching} setIsFetching={setIsFetching} drawerBodyRef={drawerBodyRef} setOpenAssetsExplorer={setOpenAssetsExplorer} />}
                        </Grid>
                    </main>
                </div>
            </BottomDrawer>
        </Fragment>
    );
};

export default ImageMapEditor;


